import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle, Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

const services = [
  "General handyman repairs", "Plumbing repairs", "Electrical repairs",
  "Painting (interior & exterior)", "Drywall & plastering", "Carpentry",
  "Pressure washing", "Gutter cleaning", "Window cleaning",
  "Lawn & garden maintenance", "End-of-lease cleaning",
  "Regular property maintenance", "TV/AV & Whitegoods installation", "Other / not sure"
];

export function ContactPage({ defaultService = "" }) {
  usePageMeta({ title: "Contact", path: "/contact", description: "Tell us what needs fixing — we'll get back to you shortly. Servicing Albury, Wodonga and a 60km radius." });
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: defaultService, message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) return toast.error("Name, email and service are required");
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source: "website-contact" });
      navigate("/thank-you", { state: { name: form.name, email: form.email, source: "contact" } });
    } catch { toast.error("Could not send. Please try calling us."); setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page">
      <section data-nav-theme="dark" className="bg-navy-deep text-white py-20 min-h-[60vh] flex items-center">
        <div className="max-w-7xl mx-auto px-5 md:px-8 w-full">
          <div className="eyebrow">Get in touch</div>
          <h1 className="font-display display-lg tracking-wide mt-3 leading-tight">Tell us what needs fixing.</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Send us the details — we'll get back to you shortly. Or call us directly if it's urgent.</p>
        </div>
      </section>
      <section data-nav-theme="light" className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="eyebrow eyebrow-dark">Phone</div>
            <a href={`tel:${BRAND.phone.replace(/\s/g,'')}`} className="font-display text-3xl tracking-wide text-navy hover:text-navy flex items-center gap-2 mt-1"><Phone size={20} weight="fill" className="text-navy"/>{BRAND.phone}</a>
          </div>
          <div>
            <div className="eyebrow eyebrow-dark">Email</div>
            <a href={`mailto:${BRAND.email}`} className="text-navy hover:text-navy flex items-center gap-2 mt-1 break-all"><EnvelopeSimple size={18} weight="fill" className="text-navy shrink-0"/>{BRAND.email}</a>
          </div>
          <div>
            <div className="eyebrow eyebrow-dark">Service area</div>
            <div className="text-navy flex items-center gap-2 mt-1"><MapPin size={18} weight="fill" className="text-navy"/>{BRAND.area}</div>
          </div>
          <div className="bg-navy text-white p-6 rounded-sm border-l-2 border-navy">
            <h3 className="font-display text-2xl tracking-wide">Need it urgent?</h3>
            <p className="text-slate-300 text-sm mt-1">Call us directly for emergency call-outs (leaks, lock-outs, broken windows).</p>
          </div>
        </div>
        <div className="lg:col-span-3">
          <form onSubmit={submit} className="bg-white border border-slate-200 p-6 md:p-8 rounded-sm space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Your name</Label><Input data-testid="contact-name" required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="rounded-sm mt-1 h-11"/></div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="rounded-sm mt-1 h-11"/></div>
              </div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Email</Label><Input data-testid="contact-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="rounded-sm mt-1 h-11"/></div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Service needed</Label>
                <Select value={form.service} onValueChange={(v)=>setForm({...form, service: v})}>
                  <SelectTrigger data-testid="contact-service" className="rounded-sm mt-1 h-11"><SelectValue placeholder="Pick a service"/></SelectTrigger>
                  <SelectContent>{services.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Tell us about the job</Label><Textarea data-testid="contact-message" rows={5} value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} placeholder="What needs doing? Include any photos by emailing us after sending this form." className="rounded-sm mt-1"/></div>
              <Button data-testid="contact-submit" type="submit" disabled={submitting} className="pill-navy">{submitting ? "Sending..." : "Send my request"}</Button>
              <p className="text-xs text-slate-500">No spam. We only contact you about your request.</p>
            </form>
        </div>
      </section>

      {/* Subscribe strip — let visitors sign up for tips & specials */}
      <SubscribeStrip />
    </div>
  );
}

function SubscribeStrip() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return toast.error("Enter a valid email");
    setSubmitting(true);
    try {
      await api.post("/public/subscribe", { email });
      setDone(true);
      toast.success("You're on the list!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        setDone(true);
        toast.success("You're already subscribed — thanks!");
      } else {
        toast.error(detail || "Could not subscribe");
      }
    } finally { setSubmitting(false); }
  };

  return (
    <section data-nav-theme="light" className="max-w-7xl mx-auto px-5 md:px-8 pb-20 w-full">
      <div className="bg-navy text-white p-8 md:p-10 rounded-sm">
        <div className="grid md:grid-cols-5 gap-6 items-center">
          <div className="md:col-span-3">
            <div className="eyebrow !text-white/55">Stay in the loop</div>
            <h3 className="font-display text-3xl md:text-4xl tracking-wide mt-2 leading-tight">Seasonal tips &amp; off-peak specials.</h3>
            <p className="text-slate-300 mt-2 text-sm">One short email a month with handy maintenance tips and the occasional deal — never spam.</p>
          </div>
          <form onSubmit={subscribe} className="md:col-span-2">
            {done ? (
              <div data-testid="sub-success" className="bg-white/10 backdrop-blur-sm border border-white/15 p-5 rounded-sm flex items-center gap-3">
                <CheckCircle size={22} weight="fill" className="text-white shrink-0"/>
                <div className="text-sm">Thanks — we'll be in touch.</div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <Input data-testid="sub-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="rounded-sm h-12 bg-white text-navy placeholder:text-slate-400 border-0 flex-1" />
                <Button data-testid="sub-submit" type="submit" disabled={submitting} className="pill-light !bg-white !text-navy hover:!bg-white/90 h-12 px-6 whitespace-nowrap">{submitting ? "Subscribing…" : "Subscribe"}</Button>
              </div>
            )}
            <p className="text-[11px] text-white/55 mt-2">By subscribing you agree to occasional emails from True North Maintenance. Unsubscribe anytime.</p>
          </form>
        </div>
      </div>
    </section>);
}

export default function Contact() {
  return <ContactPage />;
}
